import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { AuthRequiredError, unwrap } from '@truecare/api-client';
import { Alert } from '@truecare/ui';
import { api } from '../api';
import { SUPPORT_EMAIL } from '../support-email';
import { usePersonaHome } from '../auth/use-persona-home';
import { AccountProfileCard } from '../components/AccountProfileCard';
import { PortalShell } from '../components/PortalShell';
import {
  CaregiverProfileTab,
  FacilityAccountTab,
  HomecareProfileTab,
} from './me';

/**
 * /profile — the signed-in user's personal account, reached from the top-right
 * user menu (Phase 17). Split out of /me so the workspace at /me leads with the
 * user's *working* tabs (Offers / Shifts / …) and the relatively static account
 * lives in the standard top-right location.
 *
 * Persona detection + data-loading is shared with /me via `usePersonaHome`, so
 * the two routes can never drift.
 */
export const Route = createFileRoute('/profile')({
  beforeLoad: async () => {
    try {
      const me = await unwrap(api.GET('/api/v1/auth/me'));
      return { me };
    } catch (err) {
      if (err instanceof AuthRequiredError) {
        throw redirect({ to: '/sign-in' });
      }
      throw err;
    }
  },
  component: ProfilePage,
});

function ProfilePage() {
  const { me } = Route.useRouteContext();
  const {
    isCaregiver,
    isFacilityUser,
    isHomecareClient,
    caregiverQuery,
    facilityQuery,
    homecareQuery,
  } = usePersonaHome(me);

  const loading =
    (isCaregiver && caregiverQuery.isLoading) ||
    (isFacilityUser && facilityQuery.isLoading) ||
    (isHomecareClient && homecareQuery.isLoading);
  const error =
    (isCaregiver && caregiverQuery.isError) ||
    (isFacilityUser && facilityQuery.isError) ||
    (isHomecareClient && homecareQuery.isError);

  return (
    <PortalShell>
      <div className="mx-auto max-w-3xl px-6 py-10 md:py-16">
        <Link
          to="/me"
          className="inline-flex items-center gap-1.5 font-body text-sm text-ink-muted transition-colors hover:text-forest"
        >
          <span aria-hidden="true">←</span> Back to my workspace
        </Link>
        <h1 className="mt-4 font-heading text-3xl md:text-4xl text-forest">My profile</h1>
        <p className="mt-2 font-body text-sm text-ink-muted">Signed in as {me.email}</p>

        <div className="mt-8">
          {loading && <p className="font-body text-ink-muted">Loading your profile…</p>}

          {error && (
            <Alert tone="danger" title="Couldn't load your profile">
              Please refresh the page. If this keeps happening, contact us at{' '}
              {SUPPORT_EMAIL}.
            </Alert>
          )}

          {!loading && !error && isCaregiver && caregiverQuery.data && (
            <CaregiverProfileTab data={caregiverQuery.data} />
          )}
          {!loading && !error && isFacilityUser && <FacilityAccountTab />}
          {!loading && !error && isHomecareClient && homecareQuery.data && (
            <HomecareProfileTab data={homecareQuery.data} />
          )}

          {/* Any other signed-in account (e.g. an operator visiting the portal):
              the basic account surface every user has. */}
          {!isCaregiver && !isFacilityUser && !isHomecareClient && (
            <div className="flex flex-col gap-6">
              <AccountProfileCard />
            </div>
          )}
        </div>
      </div>
    </PortalShell>
  );
}
